import {
    PowerFunnelEvents,
    PowerFunnelGatedFeatures,
    PowerFunnelSurfaces,
    PowerFunnelUserStatuses,
    SubscriptionTerms,
    SubscriptionTypes,
    UpgradeFunnelEvents,
    platform,
} from './constants';
import { trackEvent } from './TrackedClick';
import { classifyFailureType } from './classifyFailureType';

// Pure factory — not a React hook. Returns `{ trackStarted, trackSucceeded, trackFailed }`
// closures bound to the context props. Caller passes the returned object's methods inline;
// if a caller ever needs stable identity for a `useEffect` dep array, wrap the result in
// `useMemo` at the call site.
export const createMonthlyUpgradeTelemetry = ({
    userSubscriptionStatus = null,
    referringSurface = null,
    gatedFeature = null,
    sourceEventId = null,
} = {}) => {
    const fire = (eventName, extra = {}) => {
        trackEvent(eventName, {
            platform,
            userSubscriptionStatus,
            referringSurface: referringSurface || PowerFunnelSurfaces.UNKNOWN,
            gatedFeature: gatedFeature || PowerFunnelGatedFeatures.WEB_DIRECT_TO_SUBSCRIPTION,
            ...(sourceEventId && { sourceEventId }),
            ...extra,
        });
    };

    const trackStarted = ({ billingCycle, currency, priceLocal, planSelected } = {}) => {
        fire(PowerFunnelEvents.POWER_CHECKOUT_STARTED, {
            planSelected,
            billingCycle,
            currency,
            priceLocal,
        });
    };

    const trackSucceeded = ({ billingCycle, currency, revenueAmount } = {}) => {
        fire(PowerFunnelEvents.POWER_PURCHASE_SUCCEEDED, {
            // Override the construction-time status: this factory is created at render with
            // the pre-payment status ("free"), and trackSucceeded fires before
            // fetchAuthenticatedUser refreshes Redux.
            // Annual → 'power'; monthly → 'monthly' (Segment uses all three tiers).
            userSubscriptionStatus: billingCycle === SubscriptionTerms.ANNUAL
                ? SubscriptionTypes.POWER
                : PowerFunnelUserStatuses.MONTHLY,
            billingCycle,
            currency,
            revenueAmount,
        });
    };

    // failure_type is snake_case to match the existing Segment payload shape used by
    // the popup upgrade flow. Other payload keys stay camelCase per the broader convention.
    const trackFailed = (error) => {
        fire(UpgradeFunnelEvents.UPGRADE_FAILED, {
            failure_type: classifyFailureType(error),
        });
    };

    return { trackStarted, trackSucceeded, trackFailed };
};
